import { captureException } from '@sentry/browser';
import { mergePolicyDetail } from 'actions/policies';
import { setRequestErrored, setRequestInProcess } from 'actions/request';
import { APIResponseError } from 'models/error';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import endpoint from 'shared/api';
import { useSafeTranslation } from 'shared/i18n';

import { AppState } from '../../reducers';
import { getPolicyDetail } from '../../selectors/policies';
import { EStudentChecklistRequestTypes } from './models';
import { StudentChecklistView } from './StudentChecklist.view';
import { getChecklistStatus } from './utils';
import { studentChecklistLookup } from './utils/studentChecklistLookup';
import { studentChecklistTitleLookup } from './utils/studentChecklistTitleLookup';

const completeStudentChecklist = async (userPolicyId: string) => {
  try {
    await endpoint.completeStudentChecklist(userPolicyId);
  } catch (err) {
    const error = err as APIResponseError;
    const errorMessage = error.response?.data?.message;
    captureException(
      `[STUDENT_CHECKLIST] Failed to update student checklist with policy id ${userPolicyId}: ${errorMessage}`
    );
    throw new Error(errorMessage);
  }
};

export const StudentChecklistContainer = () => {
  const dispatch = useDispatch();
  const { policyId } = useParams<{ policyId: string }>();
  const policy = useSelector((state: AppState) =>
    getPolicyDetail(state, policyId)
  );
  const isChecklistCompleted = Boolean(
    policy?.attributes?.studentChecklist?.isCompleted
  );
  const { currentSteps } = getChecklistStatus(
    policy?.attributes?.studentChecklist || null,
    policy?.status
  );
  const { t } = useSafeTranslation();

  const steps = studentChecklistLookup(currentSteps, t);
  const title = studentChecklistTitleLookup(currentSteps, t);

  const handleOnCompleteChecklist = async () => {
    dispatch(setRequestInProcess(true, EStudentChecklistRequestTypes.COMPLETE));
    try {
      await completeStudentChecklist(policyId);
      const { data } = await endpoint.getPolicyDetail(policyId);
      dispatch(mergePolicyDetail(data)); // TODO: remove this when we have a better way to update the policy
      dispatch(
        setRequestInProcess(false, EStudentChecklistRequestTypes.COMPLETE)
      );
      return 'SUCCESS';
    } catch (error) {
      dispatch(
        setRequestErrored(
          error as APIResponseError,
          EStudentChecklistRequestTypes.COMPLETE
        )
      );
      return 'ERROR';
    }
  };

  return (
    <StudentChecklistView
      steps={steps}
      title={title}
      {...(!isChecklistCompleted && {
        completeChecklist: handleOnCompleteChecklist,
      })}
    />
  );
};
